export const stages = ['ordered', 'confirmed', 'work', 'shipped', 'delivered'];
export const stageLabels = { ordered: 'Оформлен', confirmed: 'Подтверждён оператором', work: 'Производство', shipped: 'Передан в доставку', delivered: 'Доставлен' };
export const paymentLabels = { unconfirmed: 'Цена не подтверждена', pending: 'Ожидает оплаты', partial: 'Частичная оплата', completed: 'Оплачен', overpaid: 'Переплата', cancelled: 'Оплата отменена', refund: 'Возврат' };
export const idOf = (value) => String(value?._id || value || '');
export const roundMoney = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;
export const money = (value) => value == null || !Number.isFinite(Number(value)) ? '—' : `${Number(value).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ₽`;
export const dateTime = (value, short = false) => {
  if (!value || Number.isNaN(new Date(value).getTime())) return '—';
  return new Date(value).toLocaleString('ru-RU', short ? { day: '2-digit', month: '2-digit', year: 'numeric' } : { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
export const itemPrice = (item) => {
  for (const value of [item.bayerPrice, item.payablePrice]) if (value != null && String(value).trim() !== '' && Number.isFinite(Number(value)) && Number(value) >= 0) return roundMoney(value);
  return null;
};
export const itemName = (item) => item.orderItemType === 'work' ? item.workName || item.productName || 'Работа' : item.productName || item.productId?.fullName || item.productId?.name || item.workName || 'Товар удалён';

// ---------- погонные метры (ограды) ----------
// Количество позиции — всегда штуки (сколько оград). У «погонных» позиций
// цена одной штуки = погонные метры × цена за метр (с учётом высоты).
// Формула та же, что на сайте (front: FenceModel/utils/orderDetailsMeta.js).

const positive = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};
/** Способ расчёта, сохранённый в позиции. Пусто — не задан. */
export const savedUnit = (item) => (item?.priceUnit === 'linearMeter' || item?.priceUnit === 'piece' ? item.priceUnit : '');
/** Погонные метры на одну штуку: из позиции, иначе из калькулятора сайта. */
export const itemMeters = (item) => positive(item?.linearMeters) ?? positive(item?.calculatorData?.meters);
/** Цена за погонный метр с учётом высоты: из позиции, иначе из калькулятора сайта. */
export const itemPricePerMeter = (item) => positive(item?.pricePerMeter) ?? positive(item?.calculatorData?.pricePerMeter);
/** Цена за погонный метр до пересчёта по высоте. */
export const itemBasePricePerMeter = (item) => positive(item?.basePricePerMeter) ?? positive(item?.calculatorData?.basePricePerMeter) ?? positive(item?.productId?.pricePerMeter);
/** Высота 3D-модели, от которой считается надбавка за высоту, мм. */
export const itemBaseHeight = (item) => positive(item?.baseHeightMm) ?? positive(item?.calculatorData?.baseHeightMm);
/** Сохранённая форма ограды: участок или прямой забор. Пусто — по размерам. */
export const itemFenceLayout = (item) => {
  const value = item?.fenceLayout || item?.calculatorData?.fenceLayout || '';
  return value === 'perimeter' || value === 'straight' ? value : '';
};
/** Как считается позиция сейчас. Без сохранённого способа — по данным калькулятора. */
export const itemUnit = (item) => savedUnit(item) || (itemMeters(item) && itemPricePerMeter(item) ? 'linearMeter' : 'piece');
/** Как считает категория товара (или сам товар): ограды — по погонным метрам. */
export const categoryUnit = (item) => {
  const product = item?.productId && typeof item.productId === 'object' ? item.productId : null;
  const category = product?.category && typeof product.category === 'object' ? product.category : null;
  if (category?.priceCalc === 'linearMeter' || product?.pricingMode === 'linearMeter') return 'linearMeter';
  return 'piece';
};

/** Прямой забор: меньшая сторона не больше этой (толщина секции), мм — как на сайте. */
export const STRAIGHT_FENCE_MAX_SIDE_MM = 200;
export const FENCE_LAYOUT_LABELS = { perimeter: 'Ограда участка', straight: 'Прямой забор' };

/** Форма ограды: явная или по размерам (меньшая сторона до 200 мм — прямой забор). */
export const resolveFenceLayout = (layout, length, width) => {
  if (layout === 'perimeter' || layout === 'straight') return layout;
  const l = positive(length);
  const w = positive(width);
  if (!l && !w) return 'perimeter';
  if (!l || !w) return 'straight';
  return Math.min(l, w) <= STRAIGHT_FENCE_MAX_SIDE_MM ? 'straight' : 'perimeter';
};

/**
 * Расчёт ограды по формуле сайта.
 *  - метры: участок — 2 × (Д + Ш), калитка входит; прямой забор — длина;
 *  - цена за метр: базовая × (высота / высота модели), если ограда выше модели;
 *    ниже — базовая цена (на сайте помечается «индивидуальный расчёт»);
 *  - цена одной ограды = метры × цена за метр.
 * Размеры в мм. Чего не хватает — то возвращается пустым (null).
 */
export const calcFence = ({ layout, length, width, height, basePricePerMeter, baseHeightMm }) => {
  const l = positive(length);
  const w = positive(width);
  const h = positive(height);
  const base = positive(basePricePerMeter);
  const baseH = positive(baseHeightMm);
  const shape = resolveFenceLayout(layout, l, w);

  let metersRaw = null;
  if (shape === 'straight') metersRaw = l || w ? Math.max(l || 0, w || 0) / 1000 : null;
  else if (l && w) metersRaw = (2 * (l + w)) / 1000;

  let coef = 1;
  let note = '';
  if (h && baseH) {
    if (h > baseH + 0.5) coef = h / baseH;
    else if (h < baseH - 0.5) note = 'Высота меньше базовой — индивидуальный расчёт';
  }

  return {
    layout: shape,
    meters: metersRaw != null ? Math.round(metersRaw * 1000) / 1000 : null,
    pricePerMeter: base != null ? roundMoney(base * coef) : null,
    coef,
    note,
    total: metersRaw != null && base != null ? roundMoney(metersRaw * base * coef) : null,
  };
};

/** «12 пог. м». */
export const formatMeters = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? `${number.toLocaleString('ru-RU', { maximumFractionDigits: 3 })} пог. м` : '—';
};
/** «2 шт.». */
export const formatPieces = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? `${number.toLocaleString('ru-RU', { maximumFractionDigits: 3 })} шт.` : '—';
};
/** «×1,2». */
export const formatCoef = (value) => `×${Number(value || 1).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}`;

export const currentStage = (order) => order.currentStage || stages.slice().reverse().find((stage) => (order.orderStatus || []).some((row) => row.type === stage && row.isCompleted)) || 'ordered';
export const buyer = (order) => {
  const meta = order.officeOrderMeta || {};
  const hasMeta = !!(meta.customerName || meta.customerPhone || meta.customerEmail);
  const user = typeof order.user === 'object' ? order.user || {} : {};
  return hasMeta ? { name: meta.customerName || 'Покупатель', phone: meta.customerPhone || '', email: meta.customerEmail || '', author: [user.firstName, user.lastName].filter(Boolean).join(' ') } : { name: [user.firstName, user.lastName].filter(Boolean).join(' ') || 'Покупатель не указан', phone: user.contactNumber || '', email: user.email || '', organization: user.nameOrganization || '' };
};
export const numberLabel = (order) => [order.orderNumber || idOf(order._id).slice(-8), order.orderNumberAdditional].filter(Boolean).join(' · ');
export const localDateTime = (value = new Date()) => {
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
export const dateBoundary = (value, nextDay = false) => {
  if (!value) return undefined;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day + (nextDay ? 1 : 0)).toISOString();
};
